import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from 'ngx-toastr';
import { ApiService } from 'src/app/services/api.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PaginationHelperService } from 'src/app/services/pagination-helper.service';
import { ADMIN_PORTAL_LOGS_TABLE_HEADERS, ADMIN_PORTAL_LOGS_TABLE_SUB_HEADERS } from 'src/app/lib/constants/admin-portal.constants';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';
import { ADMIN_PORTAL_TAB_OPTIONS } from 'src/app/lib/constants/dropdown-options.constants';
import { DebounceService } from 'src/app/core/services/debounce.service';
import { RefreshService } from 'src/app/refresh.service';

@Component({
  selector: 'app-admin-portal-user-logs',
  templateUrl: './admin-portal-user-logs.component.html',
  styleUrls: ['./admin-portal-user-logs.component.scss']
})
export class AdminPortalUserLogsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  public form: UntypedFormGroup;
  public limitForm: UntypedFormGroup;  
  public searchForm: UntypedFormGroup;
  isLoading = false;
  submitted = false;
  showTable = true;

  // Tab options
  tabOptions = ADMIN_PORTAL_TAB_OPTIONS;

  // Table headers
  tableHeader = ADMIN_PORTAL_LOGS_TABLE_HEADERS;
  tableSubHeader = ADMIN_PORTAL_LOGS_TABLE_SUB_HEADERS;

  // Data
  logsList: any[] = [];

  // Pagination
  size = 10;
  total_pages = 0;
  current_page = 1;
  pages_array: number[] = [];
  total = 0;

  // Search
  search_keyword = '';

  limitKey: any;  // ADD THIS
  searchKey: any; // ADD THIS

  // Sorting
  sort_column = "dateTime";
  sort_type: 'ASC' | 'DESC' = 'ASC';

  // Filter fields - stores the submitted filter criteria
  filterFields: any = null;

  // Portal tab selection
  portalTab_text: string = null;
  portalTab: any = null;


  private resetTableState(): void{
    this.size = 10;
    this.form?.reset({
      username: null,
      dateFrom: null,
      dateTo: null
    });
    this.limitForm?.reset({ limit: 10 });
    this.limitKey = '';
    this.searchForm?.reset();
    this.search_keyword = '';
    this.portalTab = null;
    this.portalTab_text = null;
    this.total_pages = 0;
    this.filterFields = {}; // reset filter fields
    this.showTable  = false; // hides table
    this.logsList = []; // clear table
  }

  private resetLocalStorage(): void{
    localStorage.setItem('itemLists', JSON.stringify(null));
  }

  constructor(
    private fb: UntypedFormBuilder,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private apiService: ApiService,
    public paginationHelper: PaginationHelperService,
    private popUpDialogService: PopUpDialogService,
    private debounce: DebounceService,
    private refreshService: RefreshService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    // Fetch all logs on page load with empty filters
    this.submitted = true;
    this.filterFields = {};
    this.get_logs_list();

    this.refreshService.resetTable$.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.resetTableState(); //resets filters, table entries
        this.resetLocalStorage(); //resets local storage
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    this.spinner.hide().catch((err) => {
      console.error('Spinner hide error on destroy:', err);
    });
  }

  initializeForm() {
    this.form = this.fb.group({
      username: [null],
      dateFrom: [null],
      dateTo: [null],
    });

    
    this.searchForm = this.fb.group({ 
        search: ['']
    });
    
    this.limitForm = this.fb.group({
        limit: [10],
    });

    this.limitKey = this.debounce.debounce(() => {
      this.current_page = 1;
      this.size = this.limitForm.value.limit;
        this.get_logs_list();
    }, 500);
  }

  // Date validation method
  validateDates(): boolean {
    const dateFrom = this.form.get('dateFrom').value;
    const dateTo = this.form.get('dateTo').value;

    if (dateFrom && dateTo) {
      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);

      // Reset time to compare only dates
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(0, 0, 0, 0);

      if (toDate < fromDate) {
        this.toastr.error('Date To cannot be earlier than Date From', 'Invalid Date Range');
        return false;
      }
    }
    return true;
  }

  submit() {
    // Validate dates before submission
    if (!this.validateDates()) {
      return;
    }

    // Always proceed with submission - no need to check for empty filters
    this.proceedWithSubmit();
  }

  proceedWithSubmit() {
    this.submitted = true;
    this.current_page = 1;
    this.search_keyword = '';
    
    // Build filter params from form - only include fields that have values
    const params: any = {};
    
    if (this.form.controls.username.value) {
      params.username = this.form.controls.username.value;
    }
    if (this.form.controls.dateFrom.value) {
      params.dateFrom = this.form.controls.dateFrom.value;
    }
    if (this.form.controls.dateTo.value) {
      params.dateTo = this.form.controls.dateTo.value;
    }
    if (this.portalTab) {
      params.portalTab = this.portalTab;
    }

    // Store filter fields for future pagination/sorting/search
    this.filterFields = params;
        
    // Fetch the data
    this.get_logs_list();
    
    // Reset form after submission
    this.form.reset();
    this.portalTab = null;
    this.portalTab_text = null;
  }

  get_logs_list(): void {
    this.isLoading = true;
    this.spinner.show().catch((err) => {
      console.error('Spinner show error:', err);
    });

    // Build params - only include fields that exist in filterFields
    const params: any = {};
    
    if (this.filterFields) {
      if (this.filterFields.username) {
        params.username = this.filterFields.username;
      }
      if (this.filterFields.dateFrom) {
        params.dateFrom = this.filterFields.dateFrom;
      }
      if (this.filterFields.dateTo) {
        params.dateTo = this.filterFields.dateTo;
      }
      if (this.filterFields.portalTab) {
        params.portalTab = this.filterFields.portalTab;
      }
    }

    // Add search and sort if needed (currently commented out based on your code)
    if (this.search_keyword) {
      params.username = this.search_keyword;
    }
    // params.sortBy = {
    //   [this.sort_column]: this.sort_type
    // };

    this.pages_array = [];

    this.apiService.getPortalUserLogs(this.current_page, this.size, params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.status === 200) {
            this.logsList = response.result.data;
            this.showTable = true;  // Show the table again after data loads
            
            this.pages_array = this.paginationHelper.generatePageArray(
              this.current_page,
              response.result.totalPages
            );
            this.total_pages = parseInt(response.result.totalPages.toString(), 10);
            this.current_page = response.result.currentPage;
            this.total = parseInt(response.result.totalItems.toString(), 10);
            
          }
        },
        error: (error) => {
          this.toastr.error(
            `Please refresh or contact the developer if the error still occurs: ${error.message || error}`,
            'Failed Fetching Portal User Logs'
          );
          this.logsList = [];
        },
        complete: () => {
          this.isLoading = false;
          this.spinner.hide().catch((err) => {
            console.error('Spinner hide error:', err);
          });
        }
      });
  }

  onLimitChangeFromComponent(event: any): void {
  const value = parseInt(event.target.value, 10);
  
    if (!value || value <= 0) {
      this.size = 10;
      this.limitForm.patchValue({ limit: 10 });
    } else {
      this.size = value;
    }
    
    this.current_page = 1;
    this.get_logs_list();
  }

  onSearchKeyupFromComponent(event: any): void {
    this.search_keyword = event.target.value;
    this.current_page = 1;
    this.get_logs_list();
  }

  keyDownFunction(event: KeyboardEvent): void {
    if (event.keyCode === 13) {
      this.searchList();
    }
  }

  searchList(): void {
    this.current_page = 1;
    this.get_logs_list();
  }

  sortList(sort_column: string, sort_type: 'ASC' | 'DESC'): void {
    this.spinner.show().catch((err) => {
      console.error('Spinner show sort error:', err);
    });

    const { column, type } = this.paginationHelper.toggleSort(
      this.sort_column,
      this.sort_type,
      sort_column,
      sort_type
    );

    this.sort_column = column;
    this.sort_type = type;
    this.get_logs_list();
  }

  setPage(num: number): void {
    this.current_page = num;
    this.get_logs_list();
  }

  nextPage(): void {
    if (this.current_page < this.total_pages) {
      this.current_page++;
      this.get_logs_list();
    }
  }

  prevPage(): void {
    if (this.current_page > 1) {
      this.current_page--;
      this.get_logs_list();
    }
  }

  setPortalTabType(text: string, value: any): void {
    this.portalTab = value;
    this.portalTab_text = text;
  }
}
